import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useViewport } from '@/hooks';
import { ChevronRightIcon, XIcon } from '@/icons';
import { Button, type ButtonProps, Chip } from '@/ui_v3';
import { cn } from '@/utils';
export interface PromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  description: ReactNode;
  imageUrl: string;
  badge?: {
    icon?: React.JSX.Element;
    label: string;
    className?: string;
    textClassName?: string;
  };
  primaryLabel: string;
  primaryIntent?: ButtonProps['intent'];
  primaryRightIcon?: React.JSX.Element;
  primaryClassName?: string;
  onPrimaryClick: () => void;
  secondaryLabel?: string;
  onSecondaryClick?: () => void;
}
const DEFAULT_PRIMARY_RIGHT_ICON = <ChevronRightIcon size="xs" fill={ICON_COLOR.inverse} />;
export const PromotionModal = ({
  isOpen,
  onClose,
  title,
  description,
  imageUrl,
  badge,
  primaryLabel,
  primaryIntent = 'strong',
  primaryRightIcon = DEFAULT_PRIMARY_RIGHT_ICON,
  primaryClassName,
  onPrimaryClick,
  secondaryLabel,
  onSecondaryClick
}: PromotionModalProps) => {
  const {
    isMobile
  } = useViewport();
  if (!isOpen) return null;
  if (isMobile) {
    return createPortal(<div className={cn('animate-once animate-duration-500 animate-delay-[400ms] animate-ease-in', 'animate-fade-up', 'py-md px-lg', 'bottom-[25px] left-4 right-4', 'fixed rounded-md border-opacity-assistive border flex flex-col', 'z-notice bg-default', 'shadow-[0px_8px_20px_0px_rgba(28,_27,_31,_0.12),_0px_0px_16px_0px_rgba(28,_27,_31,_0.08)]', 'pointer-events-auto')}>
        <Button intent="neutral" rounded="full" size="2xs" variant="contained" className={cn('p-0 size-[36px] flex items-center justify-center', 'absolute right-[-12px] top-[-16px] w-[36px]', 'shadow-sm')} onClick={onClose}>
          <XIcon fill={ICON_COLOR.neutral} size="md" />
        </Button>
        <div className="flex flex-col gap-md">
          <div className="flex items-center justify-between gap-md">
            <div className="flex flex-col gap-2xs">
              <h3 className="text-body-lg-bold text-default">{title}</h3>
              <p className="text-body-sm-medium text-neutral">{description}</p>
            </div>
            <img src={imageUrl} className="size-[58px] shrink-0 rounded-sm object-cover object-center" alt="promotion banner" />
          </div>
          <div className="flex flex-col gap-2xs">
            <Button intent={primaryIntent} size="lg" onClick={onPrimaryClick} className={cn('w-full', primaryClassName)}>
              {primaryLabel} {primaryRightIcon}
            </Button>
            {secondaryLabel && onSecondaryClick && <Button variant="outlined" intent="neutral" size="lg" onClick={onSecondaryClick} className="w-full">
                {secondaryLabel}
              </Button>}
          </div>
        </div>
      </div>, document.body);
  }
  return <DialogPrimitive.Root open={isOpen}
  // 외부 영역 클릭·Esc 로 닫는다(Radix 는 onOpenChange 가 있어야 전파한다).
  onOpenChange={open => {
    if (!open) onClose();
  }} data-sentry-element="unknown" data-sentry-component="PromotionModal" data-sentry-source-file="PromotionModal.tsx">
      <DialogPrimitive.Portal data-sentry-element="unknown" data-sentry-source-file="PromotionModal.tsx">
        {/* dim 값·페이드는 ZUS DialogOverlay(packages/zus dialog/Dialog.tsx)와 동일하게 맞춘다. */}
        <DialogPrimitive.Overlay className={cn('fixed inset-0 z-modal bg-[rgba(28,27,31,0.24)] transition-all duration-200', 'data-[state=open]:animate-in data-[state=open]:fade-in-0', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0')} data-sentry-element="unknown" data-sentry-source-file="PromotionModal.tsx" />
        <DialogPrimitive.Content aria-describedby={undefined} className={cn('fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2', 'bg-white rounded-lg shadow-2xl overflow-hidden', 'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out', 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95', 'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]', 'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]', 'max-w-[960px] w-[calc(100vw-48px)]')} data-sentry-element="unknown" data-sentry-source-file="PromotionModal.tsx">
          <div className="flex">
            <div className="flex min-w-[360px] flex-1 shrink flex-col justify-center gap-xl px-[40px] py-2xl">
              <div className="flex flex-col gap-md">
                {badge && <Chip size="md" className={cn('rounded-xl border-alternative bg-neutral', badge.className)} leftSlot={badge.icon}>
                    <span className={cn('text-body-sm-semibold text-neutral', badge.textClassName)}>
                      {badge.label}
                    </span>
                  </Chip>}
                <h2 className="whitespace-pre-line text-heading-xl-extrabold text-default">
                  {title}
                </h2>
                <p className="whitespace-pre-line text-body-lg-medium text-neutral">
                  {description}
                </p>
              </div>
              <div className="flex flex-wrap gap-xs">
                <Button intent={primaryIntent} size="lg" onClick={onPrimaryClick} rightSlot={primaryRightIcon} className={primaryClassName} data-sentry-element="Button" data-sentry-source-file="PromotionModal.tsx">
                  {primaryLabel}
                </Button>
                {secondaryLabel && onSecondaryClick && <Button variant="outlined" intent="neutral" size="lg" onClick={onSecondaryClick}>
                    {secondaryLabel}
                  </Button>}
              </div>
            </div>

            <div className="relative h-[560px] w-[460px] shrink overflow-hidden">
              <img alt="promotion banner" src={imageUrl} className="absolute inset-0 h-full w-full object-cover object-center" />
            </div>
          </div>

          <Button intent="neutral" rounded="full" size="2xs" variant="contained" leftSlot={<XIcon fill={ICON_COLOR.neutral} size="md" />} className="absolute right-[16px] top-[16px] size-[36px] p-0" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="PromotionModal.tsx" />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>;
};